import * as React from 'react';
import { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CustomDatePicker from './CustomDatePicker';
import NavbarBreadcrumbs from './NavbarBreadcrumbs';
import MenuButton from './MenuButton';
import ColorModeIconDropdown from '../../shared-theme/ColorModeIconDropdown';
import { Box, Typography, FormControl, Select, MenuItem, Chip } from '@mui/material';
import Search from './Search';
import { fetchUserAccounts } from '../../services/api';
import { getSelectedUserAccountFromLocalStorage, setSelectedUserAccountInLocalStorage } from '../../utils/data';

// Function to get page title based on current route
const getPageTitle = (pathname, params) => {
  if (pathname === '/' || pathname === '') return 'Dashboard';
  if (pathname === '/analytics') return 'Analytics';
  if (pathname === '/scalper') return 'Scalper';
  if (pathname === '/settings') return 'Settings';
  if (pathname.startsWith('/deployments/')) {
    const deploymentId = params?.deploymentId;
    return deploymentId ? `Deployment ${deploymentId}` : 'Deployment Details';
  }
  
  // Fallback: capitalize first letter of pathname segment
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0) {
    return segments[segments.length - 1]
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }
  
  return 'Glyder';
};

export default function Header({ page }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(getSelectedUserAccountFromLocalStorage());
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    fetchUserAccountsList();
  }, []);

  const fetchUserAccountsList = async () => {
    setLoading(true);
    try {
      const response = await fetchUserAccounts();
      const accountsList = response.data?.user_accounts || [];
      setAccounts(accountsList);
      
      // If no account is selected and accounts are available, select the first one
      if (!selectedAccount && accountsList.length > 0) {
        const firstAccount = accountsList[0];
        setSelectedAccount(firstAccount);
        setSelectedUserAccountInLocalStorage(firstAccount);
      }
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (event) => {
    const selectedAccountId = event.target.value;
    const account = accounts.find(acc => acc.id === selectedAccountId);
    if (account) {
      setSelectedAccount(account);
      setSelectedUserAccountInLocalStorage(account);
      // Trigger a custom event to notify other components of the change
      window.dispatchEvent(new CustomEvent('userAccountChanged', { detail: account }));
    }
  };

  return (
    <Stack
      direction="row"
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: '100%',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        // maxWidth: { sm: '100%', md: '1700px' },
        pt: 1.5,
      }}
      spacing={2}
    >
      <Typography variant="h4" sx={{ color: 'text.secondary', fontWeight: 600 }}>
        {getPageTitle(location.pathname, params)}
      </Typography>
      
      {/* Universal User Account Selector */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* <AccountCircleIcon color="primary" /> */}
          {/* <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
            Account:
          </Typography> */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={selectedAccount?.id || ''}
              onChange={handleAccountChange}
              displayEmpty
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }
              }}
            >
              {accounts.length === 0 ? (
                <MenuItem value="" disabled>
                  {loading ? 'Loading accounts...' : 'No accounts found'}
                </MenuItem>
              ) : (
                accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="body2" sx={{ flexGrow: 1 }}>
                        {account.name}
                      </Typography>
                      <Chip 
                        label={account.broker || 'Unknown'} 
                        size="small" 
                        variant="outlined" 
                        color="primary"
                      />
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>

        <Stack direction="row" sx={{ gap: 1 }}>
          {/* <MenuButton showBadge aria-label="Open notifications">
            <NotificationsRoundedIcon />
          </MenuButton> */}
          <ColorModeIconDropdown />
        </Stack>
      </Box>
    </Stack>
  );
}
